import React from 'react';
import { Gateway, NetworkSegment, ResourceGroup } from '../../../models/master';
import { Trans, useTranslation } from 'react-i18next';
import {
  Dropdown,
  FormLabel,
  TextInput,
  Grid,
  Column,
  Accordion,
  AccordionItem,
  TextArea,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
  RadioButtonGroup,
  RadioButton,
  Tile,
  Button,
  Layer,
  Link,
} from '@carbon/react';
import { Add, Information, TrashCan } from '@carbon/react/icons';
import AddServiceEndpoint from './AddServiceEndpoint';
import { DeploymentEntry, EndpointEntry } from './RegisterExternalService';
import AddLabels from '../../../components/AddLabels/AddLabels';

const RegisterExternalServiceForm = ({
  formData,
  onChange,
  resourceGroupList,
  networkSegments,
  gateways,
  mode,
  openSelectIpModal,
  validateDeploymentIps,
}: {
  formData: any;
  onChange: (type: string, payload: any) => void;
  resourceGroupList: ResourceGroup[];
  networkSegments: NetworkSegment[];
  gateways: Gateway[];
  mode: 'addFromDetailsPage' | 'addFromContainerPage' | 'EDIT';
  openSelectIpModal: (index: number, deployment: any) => void;
  validateDeploymentIps: (
    index: number,
    list: string[],
    currentError: boolean
  ) => void;
}) => {
  const { t } = useTranslation('registerExternalService');

  const learnMoreLinkPage = {
    url:
      navigator.language === 'fr' ||
      navigator.language === 'fr-FR' ||
      navigator.language === 'fr-BE' ||
      navigator.language === 'fr-CA' ||
      navigator.language === 'fr-CH'
        ? 'https://ibm.biz/mesh-external-services-fr'
        : 'https://ibm.biz/mesh-external-services',
    label: t('optionalInformation.learnMore'),
  };

  return (
    <Grid className='register-external-service-form'>
      <Column lg={16}>
        <div className='app-details section'>
          <div className='section-header'>{t('applicationDetails.header')}</div>
          <div className='section-subheader'>
            {t('applicationDetails.subheader')}
          </div>
          <Grid>
            <Column lg={6}>
              <Dropdown
                readOnly={mode === 'addFromDetailsPage'}
                className='form-item network-segment-dropdown'
                id='select-network-segment-dropdown'
                items={networkSegments}
                itemToString={(item: any) => item?.name ?? ''}
                onChange={(e: any) =>
                  onChange('networkSegment', e.selectedItem)
                }
                size='md'
                titleText={
                  t('applicationDetails.networkSegment.label') as string
                }
                invalid={formData?.networkSegment.error}
                invalidText={t('applicationDetails.networkSegment.emptyError')}
                selectedItem={formData?.networkSegment.value}
                label={
                  t('applicationDetails.networkSegment.placeholder') as string
                }
              />
            </Column>
            <Column lg={6}>
              <div className='form-item'>
                <div className='form-item-header'>
                  <FormLabel className='label'>
                    {t('applicationDetails.app.label')}
                  </FormLabel>
                  {mode === 'addFromContainerPage' && (
                    <Toggletip className='label-toggletip'>
                      <ToggletipButton>
                        <Information size={16} />
                      </ToggletipButton>
                      <ToggletipContent>
                        {t('applicationDetails.app.tooltip')}
                      </ToggletipContent>
                    </Toggletip>
                  )}
                </div>
                <TextInput
                  size='md'
                  readOnly={mode === 'addFromDetailsPage'}
                  id='application-name-input'
                  labelText=''
                  invalid={formData?.application.error}
                  invalidText={formData?.application.errorMessage}
                  placeholder={t('applicationDetails.app.placeholder')}
                  className='app-name-input'
                  value={formData?.application.name}
                  onChange={(e: any) =>
                    onChange('applicationName', e?.target?.value)
                  }
                />
              </div>
            </Column>
          </Grid>
          {mode === 'addFromContainerPage' && (
            <div className='service-radio-buttons form-item'>
              <RadioButtonGroup
                valueSelected={formData.application.hasService ? 'yes' : 'no'}
                orientation='vertical'
                legendText={
                  <div className='form-item-header'>
                    <FormLabel className='label'>
                      {t('applicationDetails.app.provideService.label')}
                    </FormLabel>
                    <Toggletip className='label-toggletip'>
                      <ToggletipButton>
                        <Information size={16} />
                      </ToggletipButton>
                      <ToggletipContent>
                        {t('applicationDetails.app.provideService.tooltip')}
                      </ToggletipContent>
                    </Toggletip>
                  </div>
                }
                name='service-radio-btn-group'
                onChange={(value: any) => onChange('provideService', value)}
              >
                <RadioButton
                  id='yes'
                  labelText={t('applicationDetails.app.provideService.yes')}
                  value='yes'
                />
                <RadioButton
                  id='no'
                  labelText={t('applicationDetails.app.provideService.no')}
                  value='no'
                />
              </RadioButtonGroup>
            </div>
          )}
        </div>
        {formData.networkSegment.value !== null ? (
          formData.application.hasService ? (
            <div className='svc-details section'>
              <div className='section-header'>{t('serviceDetails.header')}</div>
              <div className='section-subheader'>
                {t('serviceDetails.subheader')}
              </div>

              <div className='form-item'>
                <div className='form-item-header'>
                  <FormLabel className='label'>
                    {t('serviceDetails.service.label')}
                  </FormLabel>
                  <Toggletip align='right' className='label-toggletip'>
                    <ToggletipButton>
                      <Information size={16} />
                    </ToggletipButton>
                    <ToggletipContent>
                      <Trans
                        i18nKey={t('serviceDetails.service.tooltip')}
                        components={[
                          <div className='service-name-tooltip-content'>
                            <a
                              target='_blank'
                              rel='noreferrer'
                              href={
                                'https://datatracker.ietf.org/doc/html/rfc1035'
                              }
                            ></a>
                          </div>,
                        ]}
                      />
                    </ToggletipContent>
                  </Toggletip>
                </div>
                <Grid>
                  <Column lg={6}>
                    <TextInput
                      id='service-name-input'
                      labelText=''
                      placeholder={t('serviceDetails.service.placeholder')}
                      value={formData?.service.name}
                      invalid={formData?.service.error}
                      invalidText={formData?.service.errorMessage}
                      onChange={(e: any) =>
                        onChange('serviceName', e?.target?.value)
                      }
                    />
                  </Column>
                </Grid>
              </div>

              <div className='form-item external-endpoints'>
                <div className='section-header endpoints'>
                  {t('serviceDetails.endpoints.header')}
                </div>
                <div className='section-subheader'>
                  {t('serviceDetails.endpoints.subheader')}
                </div>

                <AddServiceEndpoint
                  serviceEndpoints={formData.service.endpoints}
                  updateEndpoints={(seps: EndpointEntry[]) =>
                    onChange('endpoints', seps)
                  }
                  gatewayList={gateways}
                />
              </div>
            </div>
          ) : (
            <div className='deployments-section section'>
              <div className='section-header'>{t('deployments.header')}</div>
              <div className='section-subheader'>
                <Trans i18nKey={t('deployments.subheader')} />
              </div>
              <Grid className='deployments-list'>
                {formData.deployments.map(
                  (deployment: DeploymentEntry, index: number) => (
                    <Column lg={6}>
                      <Tile key={index} className='deployment-tile'>
                        <div className='tile-header'>
                          {t('deployments.tileHeader')}
                          {formData.deployments.length > 1 && (
                            <Button
                              kind='ghost'
                              hasIconOnly
                              size='sm'
                              iconDescription={t('deployments.remove')}
                              renderIcon={TrashCan}
                              className='remove-deployment-button'
                              onClick={() =>
                                onChange('removeDeployment', index)
                              }
                            />
                          )}
                        </div>
                        <Layer>
                          <Dropdown
                            id='select-depl-gw-dropdown'
                            className='form-item'
                            items={gateways}
                            label={t(
                              'serviceDetails.endpoints.gateways.placeholder'
                            )}
                            value={
                              deployment.gateway.id ? deployment.gateway : null
                            }
                            itemToString={(item: any) => item?.name}
                            onChange={(e: any) =>
                              onChange('deploymentGateway', {
                                value: e?.selectedItem,
                                index: index,
                              })
                            }
                            titleText={t(
                              'serviceDetails.endpoints.gateways.label'
                            )}
                          />
                        </Layer>
                        <div>
                          <FormLabel
                            className={
                              'add-ip-address-label' +
                              (deployment.gateway.id ? '' : ' disabled')
                            }
                          >
                            <div className='text'>
                              {t('deployments.addIpAddress.label')}
                            </div>
                            <Link
                              className='link'
                              onClick={() =>
                                openSelectIpModal(index, deployment)
                              }
                            >
                              {t('deployments.addIpAddress.select')}
                            </Link>
                          </FormLabel>
                          <Layer>
                            <TextArea
                              onBlur={() =>
                                validateDeploymentIps(
                                  index,
                                  deployment.ipArr,
                                  deployment.error
                                )
                              }
                              disabled={deployment.gateway.id ? false : true}
                              labelText=''
                              helperText={t(
                                'deployments.addIpAddress.helpText'
                              )}
                              placeholder={t(
                                'deployments.addIpAddress.placeholder'
                              )}
                              value={deployment.ipStr}
                              onChange={(e: any) =>
                                onChange('typeIpAddress', {
                                  value: e?.target?.value,
                                  index,
                                })
                              }
                              invalid={deployment.error}
                              invalidText={deployment.errorMessage}
                            />
                          </Layer>
                        </div>
                      </Tile>
                    </Column>
                  )
                )}
              </Grid>

              <div className='add-deployment-button'>
                <Button
                  kind='tertiary'
                  onClick={() =>
                    onChange('addDeployment', {
                      gateway: {
                        id: '',
                        name: '',
                      },
                      ipAddresses: '',
                    })
                  }
                  renderIcon={Add}
                  size='sm'
                >
                  {t('deployments.addButton')}
                </Button>
              </div>
            </div>
          )
        ) : null}
        {formData.networkSegment.value !== null && (
          <div className='optional-info'>
            <div className='section-header'>
              {t('optionalInformation.header')}
            </div>
            <Grid>
              <Column lg={12}>
                <Accordion className='additional-info-sections' size='lg'>
                  <AccordionItem
                    title={t('optionalInformation.addtlAppDetails')}
                  >
                    <div className='expanded-section'>
                      <Dropdown
                        label=''
                        readOnly={mode === 'addFromDetailsPage'}
                        id='rg-dropdown'
                        titleText={
                          <div className='dropdown-container'>
                            <label className='app-labels-input'>
                              {t('optionalInformation.appResourceGroup')}
                            </label>
                            <Link
                              className='learnMoreLink app-labels-input'
                              href={learnMoreLinkPage.url}
                              rel='noreferrer'
                              target='_blank'
                            >
                              {learnMoreLinkPage.label}
                            </Link>
                          </div>
                        }
                        items={resourceGroupList}
                        itemToString={(item: ResourceGroup) =>
                          item ? item.name : ''
                        }
                        onChange={(e: any) =>
                          onChange('resourceGroup', e?.selectedItem)
                        }
                        selectedItem={formData?.application?.resourceGroup}
                      />
                      <AddLabels
                        id='app-labels-input'
                        labelText={t('optionalInformation.appLabels')}
                        btnText={t('optionalInformation.addText')}
                        placeholder={t('optionalInformation.addLabels')}
                        onChange={(labels: string[]) =>
                          onChange('applicationLabels', labels)
                        }
                        defaultValues={formData.application.labels}
                      />
                      <TextArea
                        className='text-area'
                        enableCounter
                        maxCount={300}
                        labelText={t('optionalInformation.description')}
                        onChange={(e: any) =>
                          onChange('description', e?.target?.value)
                        }
                        placeholder={t(
                          'optionalInformation.descriptionPlaceholder'
                        )}
                        value={formData?.application?.description}
                      />
                    </div>
                  </AccordionItem>
                  {formData.application.hasService && (
                    <AccordionItem
                      title={t('optionalInformation.addtlSvcDetails')}
                    >
                      <div className='expanded-section'>
                        <AddLabels
                          id='svc-labels-input'
                          labelText={t('optionalInformation.svcLabels')}
                          btnText={t('optionalInformation.addText')}
                          placeholder={t('optionalInformation.addLabels')}
                          onChange={(labels: string[]) =>
                            onChange('serviceLabels', labels)
                          }
                          defaultValues={formData.service.labels}
                        />
                      </div>
                    </AccordionItem>
                  )}
                </Accordion>
              </Column>
            </Grid>
          </div>
        )}
      </Column>
    </Grid>
  );
};

export default RegisterExternalServiceForm;
